import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';
import {
  StaleLoaderError,
  createHostSessionExtender,
  getHostSessionExpiration,
  loadEmbedElement,
  loaderUrlFor,
} from 'platform/utilities/quick-submit-embed';
import api from '../utilities/api';
import {
  QS_EMBED_TAG,
  QS_EMBED_USER_TYPE,
  SESSION_ERROR,
  createQuickSubmitSession,
  sessionErrorKind,
} from '../utilities/quickSubmitEmbed';

const STATUS = Object.freeze({
  LOADING: 'loading',
  MOUNTED: 'mounted',
  ERROR: 'error',
});

const LOADING_MESSAGE = 'Loading QuickSubmit...';

// Draft copy, for content review.
const ERROR_COPY = Object.freeze({
  [SESSION_ERROR.GENERIC]: {
    heading: 'We can’t load QuickSubmit right now',
    body: 'We’re sorry. Something went wrong on our end. Try again.',
    action: 'Try again',
  },
  [SESSION_ERROR.SESSION_ENDED]: {
    heading: 'Your QuickSubmit session ended',
    body: 'For your security, we ended your session. Start again to keep submitting files. You may need to attach your files again.',
    action: 'Start again',
  },
  [SESSION_ERROR.STALE]: {
    heading: 'Reload this page to use QuickSubmit',
    body: 'We updated QuickSubmit since you opened this page.',
    action: 'Reload page',
  },
  [SESSION_ERROR.UNAVAILABLE]: {
    heading: 'You can’t use QuickSubmit right now',
    body: 'We can’t confirm your access to QuickSubmit. If you think this is an error, contact your organization’s administrator.',
  },
  [SESSION_ERROR.UNVERIFIED]: {
    heading: 'Verify your identity to use QuickSubmit',
    body: 'You need to verify your identity before you can submit files.',
  },
  [SESSION_ERROR.IDENTITY]: {
    heading: 'We can’t confirm your information',
    body: 'We can’t match your account to the information we need to start QuickSubmit.',
  },
});

const RETRYABLE = [SESSION_ERROR.GENERIC, SESSION_ERROR.SESSION_ENDED];

const ErrorAlert = ({ kind, moveFocus, onRetry }) => {
  const headingRef = useRef(null);
  const copy = ERROR_COPY[kind] || ERROR_COPY[SESSION_ERROR.GENERIC];
  const canRetry = RETRYABLE.includes(kind);
  const canReload = kind === SESSION_ERROR.STALE;

  // Only an alert that comes after the page loaded takes focus: it replaces
  // a live form or the Try again button, so focus would otherwise be left on
  // nothing. On first load it is static content, read in page order, so the
  // header and h1 stay before it.
  useEffect(() => {
    if (!moveFocus) return undefined;
    let live = true;
    const heading = headingRef.current;
    const focus = () => live && focusElement(heading);
    focus();
    // va-alert hides its slotted heading until it has rendered, and a hidden
    // element cannot take focus, so the first try can silently miss.
    customElements
      .whenDefined('va-alert')
      .then(() => heading.closest('va-alert')?.componentOnReady?.())
      .then(focus);
    return () => {
      live = false;
    };
  }, [moveFocus]);

  return (
    <va-alert status="error" visible data-testid={`qs-embed-error-${kind}`}>
      <h2 slot="headline" ref={headingRef} tabIndex="-1">
        {copy.heading}
      </h2>
      <p
        className={
          canRetry || canReload ? undefined : 'vads-u-margin-bottom--0'
        }
      >
        {copy.body}
      </p>
      {(canRetry || canReload) && (
        <va-button
          text={copy.action}
          onClick={canReload ? () => window.location.reload() : onRetry}
          data-testid="qs-embed-retry"
        />
      )}
    </va-alert>
  );
};

ErrorAlert.propTypes = {
  kind: PropTypes.string.isRequired,
  moveFocus: PropTypes.bool.isRequired,
  onRetry: PropTypes.func.isRequired,
};

// Writes the view fields onto the element. A field that is now absent is
// removed: a stale data-submission-id next to a new data-form-id would open
// that draft. All three go in one synchronous block, because the element
// applies attribute changes together in one microtask.
const writeView = (el, { view, formId, submissionId }) => {
  const write = (name, value) =>
    value ? el.setAttribute(name, value) : el.removeAttribute(name);
  write('data-view', view);
  write('data-form-id', formId);
  write('data-submission-id', submissionId);
};

// One live <qs-embed-arp> for the whole QuickSubmit section. A page move or
// an open eForm only rewrites its view fields, so an upload keeps going. A
// new claimant or Try again makes a new element on a new session: both are
// single-use.
const QuickSubmitEmbed = ({
  view,
  formId,
  submissionId,
  claimantId,
  loadClaimant,
  onComplete,
  onNavigate,
  onDirtyChange,
}) => {
  const rootRef = useRef(null);
  const elRef = useRef(null);
  const [{ status, errorKind, errorAfterLoad }, setState] = useState({
    status: STATUS.LOADING,
    errorKind: null,
    errorAfterLoad: false,
  });
  const [attempt, setAttempt] = useState(0);

  // Read through refs, so a new function or a new view never burns a code.
  const callbacksRef = useRef();
  callbacksRef.current = { onComplete, onNavigate, onDirtyChange };
  const viewRef = useRef();
  viewRef.current = { view, formId, submissionId };
  const loadClaimantRef = useRef();
  loadClaimantRef.current = loadClaimant;

  // The page keys this component by claimant, so a new claimant remounts it
  // and claimantId never changes here. Only Try again runs this again.
  useEffect(() => {
    let cancelled = false;
    let el = null;
    // True once the form is on the page. A failure before that, on the first
    // try, is part of the page load.
    let mounted = false;
    const abort = new AbortController();
    const fail = kind => {
      if (cancelled) return;
      el?.remove();
      elRef.current = null;
      setState({
        status: STATUS.ERROR,
        errorKind: kind,
        errorAfterLoad: mounted || attempt > 0,
      });
    };
    // A 401 or 403 from the embed ends the session. QS's authorizer denies an
    // expired QS token and API Gateway sends that as 403. ARP is asked: on an
    // expired ARP session api.js sends the browser to sign-in, as any other
    // ARP page does. Otherwise only the embed's session ended. The element
    // goes first, so its beforeunload cannot hold up sign-in.
    let sessionEnding = false;
    const endSession = async () => {
      if (cancelled || sessionEnding) return;
      sessionEnding = true;
      el?.remove();
      elRef.current = null;
      setState({
        status: STATUS.LOADING,
        errorKind: null,
        errorAfterLoad: false,
      });
      let arpSessionActive = true;
      try {
        arpSessionActive = Boolean(await api.getUser());
      } catch {
        // A failed check is not a sign-in. The rep can start again.
      }
      if (arpSessionActive) fail(SESSION_ERROR.SESSION_ENDED);
    };

    setState({
      status: STATUS.LOADING,
      errorKind: null,
      errorAfterLoad: false,
    });
    // Started now, so the prefill calls run while the session is minted. A
    // failure only means no prefill: the rep types the info in.
    const claimantPromise = Promise.resolve(
      loadClaimantRef.current?.(abort.signal) ?? null,
    ).catch(() => null);
    (async () => {
      let session;
      try {
        session = await createQuickSubmitSession(api);
      } catch (error) {
        fail(await sessionErrorKind(error));
        return;
      }
      // An expired ARP session: api.js is sending the browser to sign-in.
      if (cancelled || !session) return;
      try {
        await loadEmbedElement(loaderUrlFor(session.config), QS_EMBED_TAG);
      } catch (error) {
        fail(
          error instanceof StaleLoaderError
            ? SESSION_ERROR.STALE
            : SESSION_ERROR.GENERIC,
        );
        return;
      }
      const embedClaimant = await claimantPromise;
      if (cancelled) return;

      el = document.createElement(QS_EMBED_TAG);
      el.session = { code: session.code };
      if (embedClaimant) el.claimant = embedClaimant;

      const onActivity = createHostSessionExtender({
        ping: () => api.getUser(),
        onExpired: endSession,
      });

      const callbacks = {
        onComplete: result => callbacksRef.current.onComplete?.(result),
        onError: ({ type, response }) => {
          if (type === 'load' || type === 'render') {
            fail(SESSION_ERROR.GENERIC);
          } else if (response?.status === 401 || response?.status === 403) {
            endSession();
          }
          // A "view" error is not fatal. The embed stays on its screen.
        },
        getHostSessionExpiration,
        onActivity,
      };
      // A host that passes onNavigate owns every move, so pass it only when
      // there is one. Otherwise the embed routes on its own.
      if (callbacksRef.current.onNavigate) {
        callbacks.onNavigate = request =>
          callbacksRef.current.onNavigate?.(request);
      }
      if (callbacksRef.current.onDirtyChange) {
        callbacks.onDirtyChange = dirty =>
          callbacksRef.current.onDirtyChange?.(dirty);
      }
      el.callbacks = callbacks;

      // dataset stringifies undefined, and the loader rejects an unknown
      // theme outright. The claimant and the code never go here. The
      // claimantId does: it is an opaque id already in the URL. It goes
      // with or without the claimant, so the drafts are tagged for a
      // claimant the embed cannot prefill too.
      Object.entries({
        ...session.config,
        userType: QS_EMBED_USER_TYPE,
        claimantId,
      })
        .filter(([, value]) => value != null)
        .forEach(([name, value]) => {
          el.dataset[name] = value;
        });
      writeView(el, viewRef.current);

      // The loader keeps a loading state the host put inside the element,
      // so this spinner stays up until the form is on screen.
      const spinner = document.createElement('va-loading-indicator');
      spinner.setAttribute('message', LOADING_MESSAGE);
      el.appendChild(spinner);
      elRef.current = el;
      rootRef.current.replaceChildren(el);
      // The loader can report a failure while it is being inserted, which
      // runs fail() before this line. Marking it mounted would blank the page.
      mounted = true;
      setState(current =>
        current.status === STATUS.ERROR
          ? current
          : { status: STATUS.MOUNTED, errorKind: null, errorAfterLoad: false },
      );
    })().catch(() => fail(SESSION_ERROR.GENERIC));

    return () => {
      cancelled = true;
      abort.abort();
      // Removing the element is what unmounts the embed.
      el?.remove();
      elRef.current = null;
    };
  }, [attempt, claimantId]);

  // Same element, new view. Before the element exists this does nothing:
  // the mount effect reads the latest view when it makes the element.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const { dataset } = el;
    if (
      dataset.view === view &&
      dataset.formId === (formId || undefined) &&
      dataset.submissionId === (submissionId || undefined)
    ) {
      return;
    }
    writeView(el, { view, formId, submissionId });
  }, [view, formId, submissionId]);

  // Try again takes its own button away. The page heading stays put while
  // the form reloads, so focus goes there.
  useEffect(() => {
    if (attempt > 0 && status === STATUS.LOADING) focusElement('h1');
  }, [attempt, status]);

  return (
    <>
      {status === STATUS.LOADING && (
        <va-loading-indicator
          message={LOADING_MESSAGE}
          data-testid="qs-embed-loading"
        />
      )}
      {status === STATUS.ERROR && (
        <ErrorAlert
          kind={errorKind}
          moveFocus={errorAfterLoad}
          onRetry={() => setAttempt(n => n + 1)}
        />
      )}
      {/* React must never render children here: the embed takes this node
          over, and the embed's stylesheet is scoped to this exact id. */}
      <div
        id="qs-embed-root"
        ref={rootRef}
        hidden={status === STATUS.ERROR}
        data-testid="qs-embed-root"
      />
    </>
  );
};

QuickSubmitEmbed.propTypes = {
  view: PropTypes.string.isRequired,
  // The claimant from ?claimantId=, or null. It tags their eForm drafts.
  claimantId: PropTypes.string,
  formId: PropTypes.string,
  // (signal) => the embed's claimant, complete, or null. It may return a
  // promise.
  loadClaimant: PropTypes.func,
  submissionId: PropTypes.string,
  onComplete: PropTypes.func,
  onDirtyChange: PropTypes.func,
  onNavigate: PropTypes.func,
};

export default QuickSubmitEmbed;
